// ═══════════════════════════════════════════════════════════════════════════
// ADMIN: ACTION STATUSES + RISK COLORS
// Migration S4.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Deux pages dans ce fichier (héritage du naming) :
//   - pages.adminRiskColors : palette de couleurs par sévérité (1..16).
//   - pages.adminActionStatuses : statuts d'actions (CRUD + drag-drop).

interface RiskColorRow {
  severity: number;
  color: string;
}

interface ActionStatusEntity {
  id: number;
  name: string;
  position: number;
  color?: string | null;
  bold?: boolean;
  italic?: boolean;
  strikethrough?: boolean;
  is_final?: boolean;
  is_blocked?: boolean;
  is_system?: boolean;
}

declare global {
  interface Window {
    renderRiskColorsPage: typeof renderRiskColorsPage;
    renderStatusesPage: typeof renderStatusesPage;
    initStatusDragDrop: typeof initStatusDragDrop;
    showStatusModal: typeof showStatusModal;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: COULEURS DES RISQUES
// ═══════════════════════════════════════════════════════════════════════════

pages.adminRiskColors = async function () {
  setHeader(t('risk_colors.page_title', { risk_plural: t('vocabulary.risk_plural') }), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const rows = (await api.getRiskColors()) as RiskColorRow[];
    renderRiskColorsPage(rows);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderRiskColorsPage(initialRows: ReadonlyArray<RiskColorRow>): void {
  const canWrite = !!(window.hasPermission && window.hasPermission('risk_colors.write'));

  const state: Record<number, string> = {};
  for (const r of initialRows) state[r.severity] = r.color;

  const SEVERITIES: ReadonlyArray<number> = [1, 2, 3, 4, 6, 8, 9, 12, 16];

  const rowsHtml = SEVERITIES.map((sev) => {
    const color = state[sev] || '#cccccc';
    const disabled = canWrite ? '' : 'disabled';
    return `<tr>
      <td style="font-weight:600;font-size:16px;width:120px;text-align:center">${sev}</td>
      <td>
        <input type="color" class="rc-picker" data-sev="${sev}" value="${esc(color)}" ${disabled}
          style="width:60px;height:32px;padding:0;border:1px solid var(--border);border-radius:var(--radius);cursor:${canWrite ? 'pointer' : 'not-allowed'}">
        <input type="text" class="rc-hex" data-sev="${sev}" value="${esc(color)}" maxlength="7" ${disabled}
          pattern="^#[0-9a-fA-F]{6}$"
          style="margin-left:8px;width:90px;font-family:monospace;padding:6px 8px;border:1px solid var(--border);border-radius:var(--radius)">
      </td>
      <td>
        <span class="rc-preview" data-sev="${sev}" style="display:inline-block;width:60px;height:24px;border-radius:var(--radius);background:${esc(color)};border:1px solid var(--border)"></span>
      </td>
    </tr>`;
  }).join('');

  const saveBtn = canWrite
    ? `<button class="btn btn-primary" id="btn-rc-save" style="margin-top:16px">${esc(t('common.save'))}</button>`
    : `<p style="color:var(--text3);font-size:12px;margin-top:12px">${t('risk_colors.readonly_notice')}</p>`;

  setContent(`
    <div class="card">
      <p style="font-size:13px;color:var(--text3);margin-top:0">${esc(t('risk_colors.intro'))}</p>
      <div class="table-wrap">
        <table>
          <thead><tr>
            <th style="text-align:center">${esc(t('risk_colors.col.severity'))}</th>
            <th>${esc(t('risk_colors.col.color'))}</th>
            <th>${esc(t('risk_colors.col.preview'))}</th>
          </tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
      ${saveBtn}
    </div>`);

  function setColor(sev: number, newColor: string): boolean {
    const normalized = String(newColor || '').toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(normalized)) return false;
    state[sev] = normalized;
    const picker = document.querySelector<HTMLInputElement>(`.rc-picker[data-sev="${sev}"]`);
    const hexInp = document.querySelector<HTMLInputElement>(`.rc-hex[data-sev="${sev}"]`);
    const prev = document.querySelector<HTMLElement>(`.rc-preview[data-sev="${sev}"]`);
    if (picker) picker.value = normalized;
    if (hexInp) hexInp.value = normalized;
    if (prev) prev.style.background = normalized;
    return true;
  }

  document.querySelectorAll<HTMLInputElement>('.rc-picker').forEach((el) => {
    el.addEventListener('input', (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      if (!target) return;
      setColor(parseInt(target.dataset['sev'] || '0'), target.value);
    });
  });
  document.querySelectorAll<HTMLInputElement>('.rc-hex').forEach((el) => {
    el.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      if (!target) return;
      const sev = parseInt(target.dataset['sev'] || '0');
      if (!setColor(sev, target.value)) {
        toast(t('risk_colors.error.format'), 'error');
        target.value = state[sev] || '';
      }
    });
  });

  const btn = document.getElementById('btn-rc-save') as HTMLButtonElement | null;
  if (btn) {
    const labelSave = t('common.save');
    btn.addEventListener('click', () => {
      void (async () => {
        btn.disabled = true;
        btn.textContent = t('risk_colors.btn.saving');
        try {
          const entries = SEVERITIES.map((severity) => ({
            severity,
            color: state[severity] || '#cccccc',
          }));
          await api.updateRiskColorsBulk({ entries });
          toast(t('risk_colors.toast.saved'));
          await loadRiskColors(true);
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : t('risk_colors.toast.save_error');
          toast(msg, 'error');
        } finally {
          btn.disabled = false;
          btn.textContent = labelSave;
        }
      })();
    });
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: ACTION STATUSES
// ═══════════════════════════════════════════════════════════════════════════

pages.adminActionStatuses = async function () {
  setHeader(
    t('action_statuses.page_title'),
    `<button class="btn btn-secondary ml-auto" id="btn-ei-action-statuses" style="margin-right:6px">${esc(t('common.export_import'))}</button>` +
      `<button class="btn btn-primary" id="btn-new-status">${esc(t('action_statuses.btn.add'))}</button>`,
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const statuses = (await loadActionStatuses(true)) as ActionStatusEntity[];
    renderStatusesPage(statuses);
    document
      .getElementById('btn-new-status')
      ?.addEventListener('click', () => showStatusModal(null, statuses));
    document.getElementById('btn-ei-action-statuses')?.addEventListener('click', () => {
      const examples = window.EXPORT_EXAMPLES['action-statuses'];
      window.showExportImportModal(
        'action-statuses',
        t('action_statuses.page_title'),
        examples?.json ?? '',
        examples?.csv ?? '',
      );
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderStatusesPage(statuses: ActionStatusEntity[]): void {
  let sortKey: 'position' | 'name' = 'position';
  let sortDir: 'asc' | 'desc' = 'asc';

  const sortStatuses = (arr: ReadonlyArray<ActionStatusEntity>): ActionStatusEntity[] =>
    [...arr].sort((a, b) => {
      const va = sortKey === 'name' ? (a.name || '').toLowerCase() : a.position;
      const vb = sortKey === 'name' ? (b.name || '').toLowerCase() : b.position;
      return (va < vb ? -1 : va > vb ? 1 : 0) * (sortDir === 'asc' ? 1 : -1);
    });

  const th = (key: 'position' | 'name', label: string): string => {
    const active = sortKey === key;
    return `<th data-sort-status="${key}" style="cursor:pointer;user-select:none">${esc(label)} <span style="font-size:10px">${active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span></th>`;
  };

  const renderRows = (): string => {
    const sorted2 = sortStatuses(statuses);
    return sorted2
      .map((s) => {
        // Statut système : non supprimable (cf. user 2026-05-21 — backend
        // renvoie 409 si on tente de DELETE). Bouton ✕ désactivé visuellement
        // et tooltip explicite. Le badge « Système » identifie la ligne.
        const systemBadge = s.is_system
          ? ` <span style="background:var(--bg3);color:var(--text2);font-size:10px;padding:1px 6px;border-radius:8px;margin-left:6px;font-weight:500">${esc(t('action_statuses.system.badge'))}</span>`
          : '';
        const delBtn = s.is_system
          ? `<button class="btn btn-danger btn-icon" disabled style="margin-left:4px;opacity:.4;cursor:not-allowed" title="${esc(t('action_statuses.system.notDeletable'))}">✕</button>`
          : `<button class="btn btn-danger btn-icon" data-del-status="${s.id}" data-status-name="${esc(s.name)}" style="margin-left:4px" title="${esc(t('common.delete'))}">✕</button>`;
        return `
      <tr class="status-drag-row" draggable="${s.is_system ? 'false' : 'true'}" data-status-id="${s.id}">
        <td style="width:32px;text-align:center">${sortKey === 'position' && !s.is_system ? `<span class="drag-handle" role="button" aria-label="${esc(t('stages.dragdrop.aria_handle'))}">⠿</span>` : ''}</td>
        <td style="cursor:pointer" data-click-status="${s.id}"><strong>${esc(s.name)}</strong>${systemBadge}</td>
        <td>
          <button class="btn btn-secondary btn-icon" data-edit-status="${s.id}" title="${esc(t('common.edit'))}">✎</button>
          ${delBtn}
        </td>
      </tr>`;
      })
      .join('');
  };

  const sorted = sortStatuses(statuses);

  setContent(`
    <div class="card">
      <p style="font-size:12px;color:var(--text3);margin-bottom:12px">${esc(t('action_statuses.dragdrop.help'))}</p>
      <div class="table-wrap"><table id="statuses-table">
        <thead><tr><th style="width:32px"></th>${th('name', t('action_statuses.col.name'))}<th></th></tr></thead>
        <tbody id="statuses-tbody">${renderRows() || `<tr><td colspan="3" class="empty">${esc(t('action_statuses.empty'))}</td></tr>`}</tbody>
      </table></div>
    </div>`);

  document.querySelector('#statuses-table thead')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const th2 = target.closest<HTMLElement>('[data-sort-status]');
    if (!th2) return;
    const key = th2.dataset['sortStatus'] as 'position' | 'name';
    if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    else {
      sortKey = key;
      sortDir = 'asc';
    }
    const tbody = document.getElementById('statuses-tbody');
    if (tbody) tbody.innerHTML = renderRows();
    document.querySelectorAll<HTMLElement>('[data-sort-status]').forEach((h) => {
      const active2 = h.dataset['sortStatus'] === sortKey;
      const span = h.querySelector('span');
      if (span) span.textContent = active2 ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
    });
  });

  initStatusDragDrop(sorted);

  document.getElementById('statuses-tbody')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const clickStatus = target.closest<HTMLElement>('[data-click-status]');
    if (clickStatus && !target.closest('button')) {
      const id = parseInt(clickStatus.dataset['clickStatus'] || '0');
      const s = statuses.find((x) => x.id === id);
      if (s) showStatusModal(s, statuses);
      return;
    }
    const editBtn = target.closest<HTMLElement>('[data-edit-status]');
    if (editBtn) {
      const id = parseInt(editBtn.dataset['editStatus'] || '0');
      const s = statuses.find((x) => x.id === id);
      if (s) showStatusModal(s, statuses);
      return;
    }
    const delBtn = target.closest<HTMLElement>('[data-del-status]');
    if (delBtn) {
      const id = parseInt(delBtn.dataset['delStatus'] || '0');
      const name = delBtn.dataset['statusName'] || '';
      openModal(
        t('action_statuses.modal.delete.title'),
        `<p>${t('action_statuses.modal.delete.message', { name })}</p>`,
        async () => {
          try {
            await api.deleteActionStatus(id);
            closeModal();
            toast(t('action_statuses.toast.deleted'));
            _cache.actionStatuses = null;
            void pages.adminActionStatuses?.({});
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('common.error');
            toast(msg, 'error');
          }
        },
        t('common.delete'),
      );
    }
  });
  document
    .getElementById('btn-new-status')
    ?.addEventListener('click', () => showStatusModal(null, statuses));
}

function initStatusDragDrop(_statuses: ReadonlyArray<ActionStatusEntity>): void {
  const tbody = document.getElementById('statuses-tbody');
  if (!tbody) return;
  let dragSrc: HTMLElement | null = null;
  tbody.addEventListener('dragstart', (e: DragEvent) => {
    const target = e.target as HTMLElement | null;
    dragSrc = target?.closest<HTMLElement>('tr.status-drag-row') ?? null;
    if (dragSrc) {
      dragSrc.style.opacity = '0.4';
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    }
  });
  tbody.addEventListener('dragend', () => {
    if (dragSrc) dragSrc.style.opacity = '';
    tbody.querySelectorAll('tr.status-drag-row').forEach((r) => r.classList.remove('drag-over'));
    dragSrc = null;
  });
  tbody.addEventListener('dragover', (e: DragEvent) => {
    e.preventDefault();
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('tr.status-drag-row');
    tbody.querySelectorAll('tr.status-drag-row').forEach((r) => r.classList.remove('drag-over'));
    if (target && target !== dragSrc) target.classList.add('drag-over');
  });
  tbody.addEventListener('dragleave', (e: DragEvent) => {
    const trEl = (e.target as HTMLElement | null)?.closest<HTMLElement>('tr.status-drag-row');
    if (trEl) trEl.classList.remove('drag-over');
  });
  tbody.addEventListener('drop', (e: DragEvent) => {
    e.preventDefault();
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('tr.status-drag-row');
    tbody.querySelectorAll('tr.status-drag-row').forEach((r) => r.classList.remove('drag-over'));
    if (!target || !dragSrc || target === dragSrc) return;
    const rows = [...tbody.querySelectorAll<HTMLElement>('tr.status-drag-row')];
    const si = rows.indexOf(dragSrc);
    const ti = rows.indexOf(target);
    if (si < ti) tbody.insertBefore(dragSrc, target.nextSibling);
    else tbody.insertBefore(dragSrc, target);
    dragSrc.style.opacity = '';
    const newOrder = [...tbody.querySelectorAll<HTMLElement>('tr.status-drag-row')].map((r) =>
      parseInt(r.dataset['statusId'] || '0'),
    );
    void (async () => {
      try {
        await api.reorderActionStatuses(newOrder);
        _cache.actionStatuses = null;
        toast(t('action_statuses.toast.reordered'));
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    })();
  });
}

function showStatusModal(
  status: ActionStatusEntity | null,
  statuses: ReadonlyArray<ActionStatusEntity>,
): void {
  const isNew = !status;
  const maxPos = statuses.length ? Math.max(...statuses.map((s) => s.position)) + 1 : 0;
  const s: ActionStatusEntity = status || {
    id: 0,
    name: '',
    position: 0,
    color: '#1a1a1a',
    bold: false,
    italic: false,
    strikethrough: false,
    is_final: false,
    is_blocked: false,
  };

  const body = `
    <div class="form-group"><label>${esc(t('common.field.name'))}</label>
      <input type="text" id="sm2-name" value="${esc(s.name)}" placeholder="${esc(t('action_statuses.field.name.placeholder'))}"></div>
    <div style="margin-top:8px">
      <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-weight:400;font-size:13px">
        <input type="checkbox" id="sm2-final" ${s.is_final ? 'checked' : ''}>
        ${esc(t('action_statuses.field.is_final'))} <span style="font-size:11px;color:var(--text3)">${esc(t('action_statuses.field.is_final.note'))}</span>
      </label>
    </div>
    <div style="margin-top:6px">
      <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-weight:400;font-size:13px">
        <input type="checkbox" id="sm2-blocked" ${s.is_blocked ? 'checked' : ''}>
        ${esc(t('action_statuses.field.is_blocked'))} <span style="font-size:11px;color:var(--text3)">${esc(t('action_statuses.field.is_blocked.note'))}</span>
      </label>
    </div>
    <div class="form-row" style="margin-top:12px;align-items:flex-end">
      <div class="form-group" style="flex:1">
        <label>${esc(t('action_statuses.field.color'))}</label>
        <input type="color" id="sm2-color" value="${esc(s.color || '#1a1a1a')}" style="width:48px;height:32px;padding:2px;cursor:pointer">
      </div>
      <div class="form-group" style="flex:1">
        <label>&nbsp;</label>
        <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-weight:400">
          <input type="checkbox" id="sm2-bold" ${s.bold ? 'checked' : ''}> <strong>${esc(t('action_statuses.field.bold'))}</strong></label>
      </div>
      <div class="form-group" style="flex:1">
        <label>&nbsp;</label>
        <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-weight:400">
          <input type="checkbox" id="sm2-italic" ${s.italic ? 'checked' : ''}> <em>${esc(t('action_statuses.field.italic'))}</em></label>
      </div>
      <div class="form-group" style="flex:1">
        <label>&nbsp;</label>
        <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-weight:400">
          <input type="checkbox" id="sm2-strike" ${s.strikethrough ? 'checked' : ''}> <span style="text-decoration:line-through">${esc(t('action_statuses.field.strike'))}</span></label>
      </div>
    </div>
    <div style="margin-top:14px;padding:12px 16px;background:var(--bg3);border-radius:var(--radius);border:1px solid var(--border)">
      <div style="font-size:11px;color:var(--text3);margin-bottom:6px;text-transform:uppercase;letter-spacing:.06em">${esc(t('action_statuses.preview.label'))}</div>
      <span id="sm2-preview" style="font-size:14px">${esc(t('action_statuses.preview.sample'))}</span>
    </div>`;

  openModal(
    isNew ? t('action_statuses.modal.create.title') : t('action_statuses.modal.update.title'),
    body,
    async () => {
      const nameEl = document.getElementById('sm2-name') as HTMLInputElement | null;
      const name = nameEl?.value.trim() || '';
      if (!name) {
        toast(t('common.error.name_required'), 'error');
        return;
      }
      const pos = isNew ? maxPos : s.position;
      const colorEl = document.getElementById('sm2-color') as HTMLInputElement | null;
      const boldEl = document.getElementById('sm2-bold') as HTMLInputElement | null;
      const italicEl = document.getElementById('sm2-italic') as HTMLInputElement | null;
      const strikeEl = document.getElementById('sm2-strike') as HTMLInputElement | null;
      const finalEl = document.getElementById('sm2-final') as HTMLInputElement | null;
      const blockedEl = document.getElementById('sm2-blocked') as HTMLInputElement | null;
      const color = colorEl?.value || '#1a1a1a';
      const bold = boldEl?.checked ?? false;
      const italic = italicEl?.checked ?? false;
      const strikethrough = strikeEl?.checked ?? false;
      const is_final = finalEl?.checked ?? false;
      const is_blocked = blockedEl?.checked ?? false;
      try {
        if (isNew)
          await api.createActionStatus({
            name,
            position: pos,
            color,
            bold,
            italic,
            strikethrough,
            is_final,
            is_blocked,
          });
        else
          await api.updateActionStatus(s.id, {
            name,
            position: pos,
            color,
            bold,
            italic,
            strikethrough,
            is_final,
            is_blocked,
          });
        closeModal();
        toast(isNew ? t('action_statuses.toast.created') : t('action_statuses.toast.updated'));
        _cache.actionStatuses = null;
        void pages.adminActionStatuses?.({});
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    isNew ? t('common.add_short') : t('common.save'),
  );

  setTimeout(() => {
    const updatePreview = (): void => {
      const prev = document.getElementById('sm2-preview') as HTMLElement | null;
      if (!prev) return;
      const colorEl = document.getElementById('sm2-color') as HTMLInputElement | null;
      const boldEl = document.getElementById('sm2-bold') as HTMLInputElement | null;
      const italicEl = document.getElementById('sm2-italic') as HTMLInputElement | null;
      const strikeEl = document.getElementById('sm2-strike') as HTMLInputElement | null;
      prev.style.color = colorEl?.value || '';
      prev.style.fontWeight = boldEl?.checked ? '700' : '';
      prev.style.fontStyle = italicEl?.checked ? 'italic' : '';
      prev.style.textDecoration = strikeEl?.checked ? 'line-through' : '';
    };
    updatePreview();
    ['sm2-color', 'sm2-bold', 'sm2-italic', 'sm2-strike'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('input', updatePreview);
        el.addEventListener('change', updatePreview);
      }
    });
  }, 0);
}

// ── ESM globals shim (lot B.8 + S4.2) ──
window.renderRiskColorsPage = renderRiskColorsPage;
window.renderStatusesPage = renderStatusesPage;
window.initStatusDragDrop = initStatusDragDrop;
window.showStatusModal = showStatusModal;

export {};
